/**
 * Task #3131 — client for the WeatherKit-backed trip-weather endpoint.
 *
 * Failures (network, 5xx, parse) resolve to `{ available: false }` so
 * the WeatherCard renders nothing instead of an error state. The route
 * itself already returns 200 with `available:false` for the expected
 * unavailable cases (missing coords, out of horizon, not configured).
 */
const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export type WeatherConditionKey =
  | "clear-day"
  | "clear-night"
  | "partly-cloudy-day"
  | "partly-cloudy-night"
  | "cloudy"
  | "fog"
  | "rain"
  | "drizzle"
  | "thunderstorm"
  | "snow"
  | "sleet"
  | "wind"
  | "hot"
  | "cold"
  | "unknown";

export interface WeatherHour {
  time: string;
  temperatureC: number;
  precipitationChance: number;
  conditionCode: string;
  conditionKey: WeatherConditionKey;
  isDaylight: boolean;
}

export interface WeatherDay {
  date: string;
  conditionCode: string;
  conditionKey: WeatherConditionKey;
  highTemperatureC: number;
  lowTemperatureC: number;
  precipitationChance: number;
  sunrise: string | null;
  sunset: string | null;
}

export interface WeatherPickupDay {
  date: string;
  conditionCode: string;
  conditionKey: WeatherConditionKey;
  temperatureC: number;
  highTemperatureC: number;
  lowTemperatureC: number;
  precipitationChance: number;
  windSpeedKph: number;
  uvIndex: number;
  humidity: number;
  sunrise: string | null;
  sunset: string | null;
  isDaylight: boolean;
}

export interface WeatherTagline {
  tagline: string;
  summary: string;
}

export type TripWeatherResponse =
  | {
      available: true;
      pickupDay: WeatherPickupDay;
      hourly: WeatherHour[];
      daily: WeatherDay[];
      tagline: WeatherTagline;
    }
  | {
      available: false;
      reason?: string;
    };

export async function fetchTripWeather(
  publicId: string,
  token: string,
): Promise<TripWeatherResponse> {
  try {
    const res = await fetch(`${API_BASE}/trips/${encodeURIComponent(publicId)}/weather`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { available: false, reason: "http_error" };
    const data = (await res.json()) as TripWeatherResponse;
    return data;
  } catch {
    return { available: false, reason: "network_error" };
  }
}
